import React, { useState, useRef } from "react";
import { getInitials, getAvatarColor } from "../utils/api";
import { useApp } from "../context/AppContext";
import RegisterPatient from "../components/RegisterPatient";
import PatientDetail from "./PatientDetail";
import { patientAPI } from "../utils/api";
import { useTheme } from "../context/ThemeContext";

export default function Patients() {
  const { patients, deletePatient, importFromExcel, importBatch, loadPatients, loading, initialLoad, matchedReportId, user, pagination, searchQuery } = useApp();
  const canDelete = user?.role === "admin" || user?.role === "technician";

  const [search,    setSearch]    = useState("");
  const [searchDebounce, setSearchDebounce] = useState(null);
  const [showReg,   setShowReg]   = useState(false);
  const [selected,  setSelected]  = useState(() => sessionStorage.getItem("emr_selected_patient") || null);
  const [selectedReportId, setSelectedReportId] = useState(() => sessionStorage.getItem("emr_nav_rep") || null);
  const [showDel,   setShowDel]   = useState(null);
  const [showDeletedPanel, setShowDeletedPanel] = useState(false);
  const [deletedPatients,  setDeletedPatients]  = useState([]);
  const [deletedLoading,   setDeletedLoading]   = useState(false);
  const [restoringId,      setRestoringId]      = useState(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deletingAll,          setDeletingAll]          = useState(false);
  const [deleteAllProgress,    setDeleteAllProgress]    = useState({ current: 0, total: 0 });
  const [deletingOneId,        setDeletingOneId]        = useState(null);
  const [confirmDeleteOneId,   setConfirmDeleteOneId]   = useState(null);

  // Checkbox selection state
  const [checkedIds,     setCheckedIds]     = useState(new Set());
  const [hoveredId,      setHoveredId]      = useState(null);
  const [showBulkDel,    setShowBulkDel]    = useState(false);
  const [bulkDeleting,   setBulkDeleting]   = useState(false);
  const [bulkDelProgress,setBulkDelProgress]= useState({ current: 0, total: 0 });

  // Single import state
  const [importing,  setImporting]  = useState(false);
  const [importMsg,  setImportMsg]  = useState("");

  // Bulk import state (multiple files)
  const [bulkImporting,    setBulkImporting]    = useState(false);
  const [bulkProgress,     setBulkProgress]     = useState({ current: 0, total: 0 });
  const [bulkResults,      setBulkResults]      = useState(null);
  const [pendingFiles,     setPendingFiles]      = useState([]);   // files chosen, not yet imported
  const [singleFile,       setSingleFile]        = useState(null); // single import chosen file
  const bulkInputRef = React.useRef(null);
  const singleInputRef = React.useRef(null);
  const [showBulkModal,    setShowBulkModal]     = useState(false);

  const selectPatient = (id, reportId = null) => {
    setSelected(id);
    setSelectedReportId(reportId);
    if (id) sessionStorage.setItem("emr_selected_patient", id);
    else    sessionStorage.removeItem("emr_selected_patient");
    if (reportId) sessionStorage.setItem("emr_nav_rep", reportId);
    else          sessionStorage.removeItem("emr_nav_rep");
  };

  // Listen for navigate events with reportId (e.g. from Settings view report)
  React.useEffect(() => {
    const handler = (e) => {
      const { patientId, reportId } = e.detail || {};
      if (patientId && reportId) {
        selectPatient(patientId, reportId);
      }
    };
    window.addEventListener("emr:navigate", handler);
    return () => window.removeEventListener("emr:navigate", handler);
  }, []);

  const { t } = useTheme();
  const canImport  = user?.role === "admin" || user?.role === "technician";
  const canRestore = user?.role === "admin" || user?.role === "technician";

  const loadDeletedPatients = async () => {
    setDeletedLoading(true);
    try {
      const res = await patientAPI.listDeleted();
      setDeletedPatients(res.patients || []);
    } catch (e) {
      console.error("Could not load deleted patients", e);
    } finally {
      setDeletedLoading(false);
    }
  };

  const openDeletedPanel = () => {
    setShowDeletedPanel(true);
    setShowDeleteAllConfirm(false);
    loadDeletedPatients();
  };

  const handleRestore = async (id) => {
    setRestoringId(id);
    try {
      await patientAPI.restore(id);
      setDeletedPatients(prev => prev.filter(p => p.id !== id));
      await loadPatients(1, search);  // refresh main list so restored patient appears
    } catch (e) {
      console.error("Restore failed", e);
    } finally {
      setRestoringId(null);
    }
  };

  const handleDeleteAllPermanently = async () => {
    const ids = deletedPatients.map(p => p.id);
    setDeletingAll(true);
    setDeleteAllProgress({ current: 0, total: ids.length });

    let done = 0;
    for (const id of ids) {
      try {
        await patientAPI.delete(id, true);  // hard delete — permanent, files removed
      } catch (e) {
        console.error(`Failed to permanently delete ${id}`, e);
      }
      done += 1;
      setDeleteAllProgress({ current: done, total: ids.length });
    }

    setDeletedPatients([]);
    setDeletingAll(false);
    setShowDeleteAllConfirm(false);
  };

  const handleDeleteOnePermanently = async (id) => {
    setDeletingOneId(id);
    try {
      await patientAPI.delete(id, true);
      setDeletedPatients(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      console.error(`Failed to permanently delete ${id}`, e);
    } finally {
      setDeletingOneId(null);
      setConfirmDeleteOneId(null);
    }
  };

  if (selected) return <PatientDetail
    patientId={selected}
    onBack={() => selectPatient(null)}
    initialRepId={selectedReportId || undefined}
    initialView={selectedReportId ? "report" : undefined}
  />;

  const filtered = patients; // Server-side filtering via API

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchDebounce);
    if (!val.trim().startsWith('#')) setSelectedReportId(null);
    const t = setTimeout(async () => {
      const data = await loadPatients(1, val);
      // For lab ID search, capture matched_report_id from response
      if (val.trim().startsWith('#') && data?.matched_report_id) {
        setSelectedReportId(data.matched_report_id);
        // Auto-navigate if exactly 1 patient found
        if (data.patients?.length === 1) {
          selectPatient(data.patients[0].id, data.matched_report_id);
        }
      }
    }, 400);
    setSearchDebounce(t);
  };

  const handlePageChange = (newPage) => {
    loadPatients(newPage, search);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Checkbox logic ────────────────────────────────────────────────────────
  const toggleCheck = (e, id) => {
    e.stopPropagation();
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (checkedIds.size === filtered.length) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(filtered.map(p => p.id)));
    }
  };

  const clearSelection = () => setCheckedIds(new Set());

  // ── Bulk delete ───────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    const ids = Array.from(checkedIds);
    setBulkDeleting(true);
    setBulkDelProgress({ current: 0, total: ids.length });

    // Delete in parallel batches of 8
    const chunks = [];
    for (let i = 0; i < ids.length; i += 8) chunks.push(ids.slice(i, i + 8));

    let done = 0;
    for (const chunk of chunks) {
      await Promise.all(chunk.map(id => deletePatient(id).catch(() => {})));
      done += chunk.length;
      setBulkDelProgress({ current: Math.min(done, ids.length), total: ids.length });
    }

    setCheckedIds(new Set());
    setBulkDeleting(false);
    setShowBulkDel(false);
    // Single reload at the end to sync total count
    await loadPatients();
  };

  // ── Single import ─────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!singleFile) return;
    setImporting(true); setImportMsg("");
    try {
      const res = await importFromExcel(singleFile);
      setImportMsg(`✓ Imported: ${res.patient_name} → ${res.new_emr_id}`);
      setSingleFile(null);
      setImporting(false);
      loadPatients(1, "");  // reload in background — don't await
      return;
    } catch (err) {
      setImportMsg(`⚠ ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  // ── Bulk import — multiple files ──────────────────────────────────────────
  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) setPendingFiles(files);
    e.target.value = "";
  };

  const handleBulkImport = async () => {
    const files = pendingFiles;
    if (!files.length) return;

    const CHUNK_SIZE = 50;  // import 50 files at a time
    const chunks = [];
    for (let i = 0; i < files.length; i += CHUNK_SIZE) {
      chunks.push(files.slice(i, i + CHUNK_SIZE));
    }

    setBulkImporting(true);
    setBulkProgress({ current: 0, total: files.length });
    setBulkResults(null);

    const results = {
      imported: [], duplicates: [], errors: [],
      total_attempted: files.length, total_imported: 0, total_skipped: 0,
    };

    try {
      let done = 0;
      for (let ci = 0; ci < chunks.length; ci++) {
        const chunk = chunks[ci];
        try {
          const res = await importBatch(chunk);
          results.imported.push(...(res.imported || []));
          results.duplicates.push(...(res.duplicates || []));
          results.errors.push(...(res.errors || []));
          results.total_imported += res.total_imported || 0;
          results.total_skipped  += res.total_skipped  || 0;
        } catch (chunkErr) {
          // Mark all files in failed chunk as errors
          chunk.forEach(f => results.errors.push({ filename: f.name, reason: chunkErr.message }));
        }
        done += chunk.length;
        setBulkProgress({ current: done, total: files.length });
        // Small pause between chunks — lets server breathe and DB settle
        if (ci < chunks.length - 1) await new Promise(r => setTimeout(r, 800));
      }

      // Reload patients after ALL chunks complete
      await loadPatients(1, "");
      setBulkResults(results);
      setShowBulkModal(true);
    } finally {
      setBulkImporting(false);
      setPendingFiles([]);
    }
  };

  // ── Template download ─────────────────────────────────────────────────────
  const handleDownloadTemplate = () => {
    const BASE = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app");
    window.open(`${BASE}/excel/template`, "_blank");
  };

  const allChecked = filtered.length > 0 && checkedIds.size === filtered.length;
  const someChecked = checkedIds.size > 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 2 }}>{t.patients}</h1>
          <p style={{ color: "#64748B", fontSize: 13 }}>{pagination.total} registered patients</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {canImport && <button className="btn btn-ghost btn-sm" onClick={handleDownloadTemplate} title="Download import template">{t.template}</button>}

          {/* Single import */}
          {canImport && (singleFile ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--primary-l)", border: "1px solid var(--primary)", borderRadius: 8, padding: "4px 10px", fontSize: 12 }}>
              <span style={{ color: "var(--primary)", fontWeight: 600, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{singleFile.name}</span>
              <button onClick={handleImport} disabled={importing} style={{ background: "var(--primary)", color: "#fff", border: "none", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                {importing ? "Importing..." : "Import"}
              </button>
              <button onClick={() => { setSingleFile(null); setImportMsg(""); }} disabled={importing} style={{ background: "transparent", border: "none", color: "var(--slate-500)", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 2px" }} title="Clear">✕</button>
            </div>
          ) : (
            <label className="btn btn-secondary btn-sm" style={{ cursor: "pointer" }} title="Import one patient Excel file">
              {t.importOne}
              <input type="file" accept=".xlsx" style={{ display: "none" }} onChange={e => { if(e.target.files[0]) setSingleFile(e.target.files[0]); e.target.value=""; }} />
            </label>
          ))}

          {/* Bulk import */}
          {canImport && (pendingFiles.length > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--primary-l)", border: "1px solid var(--primary)", borderRadius: 8, padding: "4px 10px", fontSize: 12 }}>
              <span style={{ color: "var(--primary)", fontWeight: 600 }}>{pendingFiles.length} files</span>
              <button onClick={handleBulkImport} disabled={bulkImporting} style={{ background: "var(--primary)", color: "#fff", border: "none", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                {bulkImporting ? "Importing..." : "Import All"}
              </button>
              <button onClick={() => setPendingFiles([])} disabled={bulkImporting} style={{ background: "transparent", border: "none", color: "var(--slate-500)", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 2px" }} title="Clear">✕</button>
            </div>
          ) : (
            <label className="btn btn-secondary btn-sm" style={{ cursor: bulkImporting ? "not-allowed" : "pointer", opacity: bulkImporting ? 0.7 : 1 }} title="Select multiple Excel files to import all at once">
              {bulkImporting ? t.loading : t.bulkImport}
              <input type="file" accept=".xlsx" multiple style={{ display: "none" }} onChange={e => { const f = Array.from(e.target.files); if(f.length) setPendingFiles(f); e.target.value=""; }} disabled={bulkImporting} />
            </label>
          ))}

          {canRestore && (
            <button className="btn btn-secondary btn-sm" onClick={openDeletedPanel} title="View and restore deleted patients">
              {t.deletedPatients}
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setShowReg(true)}>{t.registerNew}</button>
        </div>
      </div>

      {/* Single import message */}
      {importMsg && (
        <div className={`alert ${importMsg.startsWith("✓") ? "alert-green" : "alert-amber"}`} style={{ marginBottom: 16 }}>
          {importMsg}
        </div>
      )}

      {/* Import progress */}
      {(importing || bulkImporting) && (
        <div style={{ marginBottom: 16, padding: "12px 16px", background: "var(--primary-l)", border: "1px solid var(--primary)", borderRadius: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--primary)", fontWeight: 600, marginBottom: 8 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 10, height: 10, border: "2px solid var(--primary)", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
              {importing ? "Importing patient..." : bulkProgress.total > 0 ? `Importing ${bulkProgress.current} / ${bulkProgress.total} files...` : `Importing ${pendingFiles.length} patients...`}
            </span>
            {bulkProgress.total > 0 && <span>{Math.round((bulkProgress.current / bulkProgress.total) * 100)}%</span>}
          </div>
          <div style={{ height: 5, background: "rgba(79,70,229,0.15)", borderRadius: 99, overflow: "hidden" }}>
            {bulkProgress.total > 0 ? (
              <div style={{ height: "100%", borderRadius: 99, background: "var(--primary)", width: `${(bulkProgress.current / bulkProgress.total) * 100}%`, transition: "width 0.3s ease" }} />
            ) : (
              <div style={{ height: "100%", borderRadius: 99, background: "var(--primary)", width: "40%", animation: "slide 1.2s ease-in-out infinite" }} />
            )}
          </div>
        </div>
      )}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slide { 0% { margin-left: -40%; } 100% { margin-left: 140%; } }
      `}</style>

      {/* Bulk delete toolbar — appears when items are checked */}
      {someChecked && canDelete && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, marginBottom: 12 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#DC2626" }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: "#DC2626", flex: 1 }}>
            {checkedIds.size} patient{checkedIds.size > 1 ? "s" : ""} selected
          </span>
          <button className="btn btn-ghost btn-sm" onClick={clearSelection} style={{ color: "#64748B" }}>
            Clear selection
          </button>
          <button className="btn btn-danger btn-sm" onClick={() => setShowBulkDel(true)}>
            🗑 Delete {checkedIds.size} patient{checkedIds.size > 1 ? "s" : ""}
          </button>
        </div>
      )}

      {/* Search */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ padding: "14px 20px" }}>
          <div className="search-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input className="input" value={search} onChange={e => handleSearch(e.target.value)} placeholder={t.searchPlaceholder} />
          </div>
        </div>
      </div>

      {/* Patient table */}
      <div className="card">
        {(loading || initialLoad) ? (
          <div className="empty-state">
            <div style={{ width: 28, height: 28, borderRadius: "50%", border: "2.5px solid #E2E8F0", borderTopColor: "#4F46E5", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
            <p style={{ color: "#94A3B8" }}>Loading patients...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
            <h3>{search ? "No patients found" : "No patients registered"}</h3>
            <p>{search ? "Try a different search term." : "Register your first patient to get started."}</p>
          </div>
        ) : (
          <div>
            {/* Table header */}
            <div style={{ display: "grid", gridTemplateColumns: "36px 2.5fr 110px 110px 150px 70px 1.2fr 110px", columnGap: 12, padding: "10px 20px", background: "var(--slate-50)", borderBottom: "1px solid var(--slate-200)", alignItems: "center" }}>
              {/* Checkbox column — invisible spacer for non-canDelete roles */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", visibility: canDelete ? "visible" : "hidden" }}>
                {canDelete && (
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#4F46E5" }}
                    title={allChecked ? "Deselect all" : "Select all"}
                  />
                )}
              </div>
              {["Patient","EMR ID","Age / Sex","Phone","Blood","Doctor","Last Visit"].map(h => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "var(--slate-400)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</div>
              ))}
            </div>

            {/* Rows */}
            {filtered.map(p => {
              const totalReports = p.report_count || p.reports?.length || 0;

              const isChecked = checkedIds.has(p.id);
              const isHovered = hoveredId === p.id;
              const showCheckbox = isChecked || isHovered || someChecked;

              return (
                <div
                  key={p.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "36px 2.5fr 110px 110px 150px 70px 1.2fr 110px",
                    columnGap: 12,
                    padding: "13px 20px",
                    borderBottom: "1px solid #F1F5F9",
                    alignItems: "center",
                    cursor: "pointer",
                    transition: "background 0.12s",
                    background: isChecked ? "var(--primary-l)" : "var(--white)",
                  }}
                  onClick={() => { if (!isChecked) selectPatient(p.id, matchedReportId || null); }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#EEF2FF"; setHoveredId(p.id); }}
                  onMouseLeave={e => { e.currentTarget.style.background = isChecked ? "var(--primary-l)" : "#fff"; setHoveredId(null); }}
                >
                  {/* Checkbox cell */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", visibility: canDelete ? "visible" : "hidden" }} onClick={e => e.stopPropagation()}>
                    {canDelete && (
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={e => toggleCheck(e, p.id)}
                        style={{
                          width: 15, height: 15, cursor: "pointer", accentColor: "#4F46E5",
                          opacity: showCheckbox ? 1 : 0,
                          transition: "opacity 0.15s",
                        }}
                      />
                    )}
                  </div>

                  {/* Patient name */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: getAvatarColor(p.name).bg, color: getAvatarColor(p.name).color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{getInitials(p.name)}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                      <div style={{ display: "flex", gap: 8, marginTop: 2, alignItems: "center" }}>
                        <span style={{ fontSize: 11, color: "var(--slate-500)" }}>{totalReports} report{totalReports !== 1 ? "s" : ""}</span>
                        {p.last_report_date && <span style={{ fontSize: 11, color: "var(--slate-400)" }}>· {p.last_report_date}</span>}
                      </div>
                    </div>
                  </div>

                  {/* EMR ID */}
                  <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                    <span
                      className="badge badge-blue"
                      style={{ fontFamily:"monospace", fontSize:11, cursor:"pointer", userSelect:"none" }}
                      title="Click to copy EMR ID"
                      onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(p.id); const el = e.currentTarget; el.textContent="Copied!"; setTimeout(() => el.textContent=p.id, 1500); }}
                    >{p.id}</span>
                  </div>

                  <div style={{ fontSize: 13, color: "#475569" }}>{p.age}y · {p.sex}</div>
                  <div
  style={{ fontSize: 12, color: "#475569", cursor: p.phone ? "pointer" : "default", userSelect: "none" }}
  title={p.phone ? "Click to copy phone" : ""}
  onClick={e => {
    e.stopPropagation();
    if (!p.phone) return;
    navigator.clipboard.writeText(p.phone);
    const el = e.currentTarget;
    const orig = el.textContent;
    el.textContent = "Copied!";
    el.style.color = "#16A34A";
    setTimeout(() => { el.textContent = orig; el.style.color = "#475569"; }, 1500);
  }}
>{p.phone || "—"}</div>
                  <div style={{ fontSize: 13, color: "var(--slate-600)", fontWeight: 500 }}>{p.blood || "—"}</div>
                  <div
  style={{ fontSize: 12, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: p.doctor ? "pointer" : "default", userSelect: "none" }}
  title={p.doctor ? "Click to copy doctor name" : ""}
  onClick={e => {
    e.stopPropagation();
    if (!p.doctor) return;
    navigator.clipboard.writeText(p.doctor);
    const el = e.currentTarget;
    const orig = el.textContent;
    el.textContent = "Copied!";
    el.style.color = "#16A34A";
    setTimeout(() => { el.textContent = orig; el.style.color = "#64748B"; }, 1500);
  }}
>{p.doctor || "—"}</div>

                  {/* Last Visit */}
                  <div style={{ fontSize: 12, color: "var(--slate-500)" }} onClick={e => e.stopPropagation()}>
                    {p.last_report_date
                      ? <span style={{ background: "var(--slate-100)", padding: "2px 8px", borderRadius: 5, fontSize: 11, color: "var(--slate-600)", fontWeight: 500 }}>{p.last_report_date}</span>
                      : <span style={{ color: "var(--slate-300)", fontSize: 11 }}>—</span>
                    }
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", background: "#F8F9FC", borderTop: "1px solid #E4E8F0", borderRadius: "0 0 12px 12px" }}>
          <div style={{ fontSize: 13, color: "#64748B" }}>
            Showing {((pagination.page - 1) * 20) + 1}–{Math.min(pagination.page * 20, pagination.total)} of {pagination.total} patients
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page <= 1}>← Prev</button>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === pagination.total_pages || Math.abs(p - pagination.page) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx-1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) => p === "..." ? (
                <span key={i} style={{ padding: "4px 8px", fontSize: 13, color: "#94A3B8" }}>...</span>
              ) : (
                <button key={i} className={`btn btn-sm ${p === pagination.page ? "btn-primary" : "btn-ghost"}`}
                  onClick={() => handlePageChange(p)}>{p}</button>
              ))
            }
            <button className="btn btn-ghost btn-sm" onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.total_pages}>Next →</button>
          </div>
        </div>
      )}

      {/* Register modal */}
      {showReg && <RegisterPatient
        onClose={() => setShowReg(false)}
        onDone={(id) => { setShowReg(false); selectPatient(id); }}
        onOpenPatient={(id) => { setShowReg(false); selectPatient(id); }}
      />}

      {/* Deleted patients panel */}
      {showDeletedPanel && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Deleted patients</div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                  Soft-deleted records — restore brings them back with all reports intact
                </div>
              </div>
              <button className="btn btn-icon btn-ghost" onClick={() => setShowDeletedPanel(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: 440, overflowY: "auto" }}>
              {deletedLoading ? (
                <div style={{ textAlign: "center", padding: "24px 0", color: "#94A3B8", fontSize: 13 }}>Loading...</div>
              ) : deletedPatients.length === 0 ? (
                <div className="empty-state"><p>{t.noDeletedPatients}</p></div>
              ) : (
                <>
                  {showDeleteAllConfirm && (
                    <div style={{ border: "1px solid #FECACA", background: "#FEF2F2", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#DC2626", marginBottom: 6 }}>
                        Permanently delete all {deletedPatients.length} patients?
                      </div>
                      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>
                        This removes all records and their files completely — they cannot be recovered after this.
                      </div>
                      {deletingAll && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748B", marginBottom: 4 }}>
                            <span>Deleting...</span>
                            <span>{deleteAllProgress.current} / {deleteAllProgress.total}</span>
                          </div>
                          <div style={{ height: 5, background: "#FECACA", borderRadius: 99, overflow: "hidden" }}>
                            <div style={{ height: "100%", borderRadius: 99, background: "#DC2626", width: `${deleteAllProgress.total > 0 ? (deleteAllProgress.current / deleteAllProgress.total) * 100 : 0}%`, transition: "width 0.3s ease" }} />
                          </div>
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setShowDeleteAllConfirm(false)} disabled={deletingAll}>Cancel</button>
                        <button className="btn btn-danger btn-sm" onClick={handleDeleteAllPermanently} disabled={deletingAll}>
                          {deletingAll ? "Deleting..." : `Yes, delete all ${deletedPatients.length} permanently`}
                        </button>
                      </div>
                    </div>
                  )}
                  {deletedPatients.map(p => (
                    <div key={p.id} style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                      border: "0.5px solid #F1F5F9", borderRadius: 8, marginBottom: 8,
                      background: "#FAFAFA",
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#0F172A" }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                          {p.id} · {p.age ? p.age + "y" : "—"} · {p.sex || "—"} · {p.phone || "—"}
                        </div>
                        <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>
                          Deleted by {p.deleted_by || "unknown"} on {(p.deleted_at || "").slice(0, 10)}
                        </div>
                        {confirmDeleteOneId === p.id && (
                          <div style={{ marginTop: 8, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, padding: "8px 10px" }}>
                            <div style={{ fontSize: 11, color: "#DC2626", fontWeight: 600, marginBottom: 6 }}>Permanently delete this patient?</div>
                            <div style={{ display: "flex", gap: 6 }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDeleteOneId(null)} disabled={deletingOneId === p.id}>Cancel</button>
                              <button className="btn btn-danger btn-sm" onClick={() => handleDeleteOnePermanently(p.id)} disabled={deletingOneId === p.id}>
                                {deletingOneId === p.id ? "Deleting..." : "Yes, delete"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleRestore(p.id)}
                          disabled={restoringId === p.id || deletingOneId === p.id}
                        >
                          {restoringId === p.id ? t.restoring : t.restore}
                        </button>
                        {canDelete && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => setConfirmDeleteOneId(confirmDeleteOneId === p.id ? null : p.id)}
                            disabled={restoringId === p.id || deletingOneId === p.id}
                            style={{ fontSize: 11 }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowDeletedPanel(false)}>Close</button>
              {deletedPatients.length > 0 && canDelete && !showDeleteAllConfirm && (
                <button className="btn btn-danger" onClick={() => setShowDeleteAllConfirm(true)}>
                  Delete all permanently
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Single delete confirm */}
      {showDel && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header"><div style={{ fontWeight: 700, fontSize: 16 }}>Delete Patient</div></div>
            <div className="modal-body">
              <div className="alert alert-red">This will permanently delete the patient and all their reports. This action cannot be undone.</div>
              <div style={{ fontSize: 13 }}>Patient: <b>{patients.find(p => p.id === showDel)?.name}</b></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowDel(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => { deletePatient(showDel); setShowDel(null); }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirm */}
      {showBulkDel && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header"><div style={{ fontWeight: 700, fontSize: 16 }}>Delete {checkedIds.size} Patients</div></div>
            <div className="modal-body">
              <div className="alert alert-red">
                This will permanently delete <b>{checkedIds.size} patients</b> and all their reports. This action cannot be undone.
              </div>
              {bulkDeleting && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#475569", marginBottom: 6 }}>
                    <span>Deleting...</span>
                    <span>{bulkDelProgress.current} / {bulkDelProgress.total}</span>
                  </div>
                  <div style={{ height: 6, background: "#E4E8F0", borderRadius: 99, overflow: "hidden" }}>
                    <div style={{ height: "100%", borderRadius: 99, background: "#DC2626", width: `${bulkDelProgress.total > 0 ? (bulkDelProgress.current / bulkDelProgress.total) * 100 : 0}%`, transition: "width 0.3s ease" }} />
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowBulkDel(false)} disabled={bulkDeleting}>Cancel</button>
              <button className="btn btn-danger" onClick={handleBulkDelete} disabled={bulkDeleting}>
                {bulkDeleting ? "Deleting..." : `Delete ${checkedIds.size} Patients`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk import results modal */}
      {showBulkModal && bulkResults && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <div style={{ fontWeight: 700, fontSize: 16 }}>Bulk Import Results</div>
            </div>
            <div className="modal-body" style={{ maxHeight: 440, overflowY: "auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
                {[
                  { label: "Imported",  value: bulkResults.total_imported, color: "#16A34A" },
                  { label: "Skipped",   value: bulkResults.total_skipped,  color: "#4F46E5" },
                  { label: "Errors",    value: bulkResults.errors?.length || 0, color: "#DC2626" },
                ].map(s => (
                  <div key={s.label} style={{ background: "#F8FAFC", borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase" }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {bulkResults.imported?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#16A34A", marginBottom: 6 }}>✓ Successfully Imported</div>
                  {bulkResults.imported.map((p, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "#F0FDF4", borderRadius: 6, marginBottom: 4, fontSize: 13 }}>
                      <span style={{ fontWeight: 600, flex: 1 }}>{p.name}</span>
                      <span style={{ fontFamily: "monospace", color: "#16A34A", marginRight: 8 }}>{p.new_emr_id}</span>
                      {p.reports_imported > 0 && <span style={{ fontSize: 11, color: "#64748B" }}>{p.reports_imported} report{p.reports_imported > 1 ? "s" : ""}</span>}
                    </div>
                  ))}
                </div>
              )}
              {bulkResults.duplicates?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#4F46E5", marginBottom: 6 }}>⚠ Skipped — Duplicates</div>
                  {bulkResults.duplicates.map((p, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: "#EFF6FF", borderRadius: 6, marginBottom: 4, fontSize: 13 }}>
                      <span style={{ fontWeight: 600 }}>{p.filename}</span>
                      <span style={{ color: "#94A3B8", fontSize: 11 }}>{p.reason}</span>
                    </div>
                  ))}
                </div>
              )}
              {bulkResults.errors?.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#DC2626", marginBottom: 6 }}>✗ Errors</div>
                  {bulkResults.errors.map((e, i) => (
                    <div key={i} style={{ padding: "6px 10px", background: "#FEF2F2", borderRadius: 6, marginBottom: 4, fontSize: 13 }}>
                      <span style={{ fontWeight: 600 }}>{e.filename}: </span>
                      <span style={{ color: "#DC2626" }}>{e.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => { setShowBulkModal(false); setBulkResults(null); }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}